import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Layout,
  Collapse,
  Form,
  Select,
  message,
  Row,
  Col,
  DatePicker,
  TimePicker,
  Radio,
  InputNumber,
  Button,
  Upload,
  Input,
  Checkbox,
} from "antd";

import {
  CameraTwoTone,
  CheckCircleOutlined,
} from "@ant-design/icons";

import Navbar from "../../../atoms/navbar";

import API from "../../../services";
import HeaderFarms from "../../../utils/header_farms";
import { requestToken } from "../../../auth/AuthServices";
import { checkNetworkType, checkNetworkBeforeSubmit } from "../../../utils/check_network";
import DistributionSelect from "../fuel-components/distributionSelect";
import {
  calculateFuelDeliveryPeriod,
  handleAmountSubmittedChange,
} from "../fuel-components/calculateFuelDeliveryPeriod";
import {
  getGeoLocation,
  watermarkImageFile,
  extractGpsFromImage,
} from "../../../utils/imageWatermark";
import { getGlobalGeoEnabled } from "../../../utils/watermarkSettings";


import { Detector } from "react-detect-offline";
import { Confirm, Loading, Report } from "notiflix";
import { useNavigate } from "react-router";
import moment from "moment";



// ---------- Helpers for Upload <-> Form binding ----------
const normFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList ?? [];
};

function CreateRequestDeliveryFuel() {
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const isAndroid = /Android/i.test(navigator.userAgent);

  const { Panel } = Collapse;
  const { TextArea } = Input;
  const { RangePicker } = DatePicker;

  const operatorInputRef = useRef(null);
  const submitButtonRef = useRef(null); // 1. Tambahkan ref untuk tombol submit

  const [operators, setOperators] = useState([]);
  const [fileListOperator, setFileListOperator] = useState([]);
  const [fileListMeter, setFileListMeter] = useState([]);
  const [geo, setGeo] = useState(null);
  const [loadingOperator, setLoadingOperator] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [loadingFlow, setLoadingFlow] = useState(false);

  const [fuelLoss, setFuelLoss] = useState(false);
  const [backDate, setBackDate] = useState(true);

  // ---------- Derived helpers ----------
  const handleDistributionChange = useCallback(async () => {
    await calculateFuelDeliveryPeriod(form);
    handleAmountSubmittedChange(form);
  }, [form]);

  const handleFormChange = useCallback(() => {
    handleAmountSubmittedChange(form);
  }, [form]);

  // ---------- Upload with watermark ----------
  const processUploadWithWatermark = useCallback(
    (setter, loadingSetter) =>
      async (info) => {
        try {
          loadingSetter(true);
          const list = [...info.fileList].slice(-1); // keep latest only

          if (list.length && list[0].originFileObj && !list[0].__watermarked) {
            const geoEnabled = getGlobalGeoEnabled();
            if (!geoEnabled) {
              setter(list);
              return;
            }
            const currentGeo =
              geo ||
              (await getGeoLocation({ timeout: 3000 })) ||
              (await extractGpsFromImage(list[0].originFileObj));

            let locName = form.getFieldValue?.("location");
            let city, district;

            if (currentGeo) {
              const key = `addr_cache:${currentGeo.lat?.toFixed(3)},${currentGeo.lng?.toFixed(3)}`;
              let cached = null;
              try {
                cached = localStorage.getItem(key);
              } catch { }

              if (!cached && navigator.onLine) {
                try {
                  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${currentGeo.lat}&lon=${currentGeo.lng}&addressdetails=1`;
                  const res = await fetch(url, { headers: { Accept: "application/json" } });
                  if (res.ok) {
                    const json = await res.json();
                    const addr = json.address || {};
                    const city_ =
                      addr.city ||
                      addr.town ||
                      addr.village ||
                      addr.municipality ||
                      addr.county ||
                      null;
                    const district_ =
                      addr.suburb ||
                      addr.state_district ||
                      addr.city_district ||
                      addr.district ||
                      addr.neighbourhood ||
                      null;
                    const displayName =
                      json.name ||
                      addr.hamlet ||
                      addr.suburb ||
                      addr.village ||
                      addr.town ||
                      addr.city ||
                      json.display_name ||
                      null;
                    cached = JSON.stringify({ city: city_, district: district_, displayName });
                    try {
                      localStorage.setItem(key, cached);
                    } catch { }
                  }
                } catch { }
              }

              if (cached) {
                const data = JSON.parse(cached);
                if (!locName) locName = data.displayName;
                city = data.city;
                district = data.district;
              }
            }

            const meta = {
              lat: currentGeo?.lat,
              lng: currentGeo?.lng,
              timestamp: Date.now(),
              locationName: locName,
              city,
              kecamatan: district,
            };

            const { file: wmFile, objectUrl } = await watermarkImageFile(
              list[0].originFileObj,
              meta,
              { maxWidth: 2000, includeMap: geoEnabled, mapZoom: 16, logoUrl: "/logo192.png", showLatLon: geoEnabled }
            );

            // Revoke previous object URL to avoid leaks
            if (list[0].url && list[0].url.startsWith("blob:")) {
              try { URL.revokeObjectURL(list[0].url); } catch { }
            }

            list[0].originFileObj = wmFile;
            list[0].thumbUrl = objectUrl;
            list[0].url = objectUrl;
            list[0].name = wmFile.name;
            list[0].__watermarked = true;
          }

          setter(list);
        } catch (e) {
          console.error("watermark error", e);
          setter([...info.fileList].slice(-1));
        } finally {
          loadingSetter(false);
        }
      },
    [geo, form]
  );

  const handleUploadMeter = processUploadWithWatermark(setFileListMeter, setLoadingFlow);
  const handleUploadOperator = processUploadWithWatermark(setFileListOperator, setLoadingOperator);

  // ---------- Data ----------
  const getOperator = (token) => {
    API.Operators(HeaderFarms(token), null)
      .then((res) => {
        setOperators(res.data || []);
      })
      .catch((err) => {
        console.error(err);
        Report.warning(
          "Oops.. something wrong",
          "Sorry, this application is experiencing problems",
          "Okay"
        );
      });
  };

  const handleChangeOperator = (employeeId) => {
    form.setFieldsValue({ operator_id: employeeId });
  };

  const infoCompleteAndRedirect = () => {
    Report.success(
      `Has been recorded.`,
      "Berhasil di tersimpan",
      "Okay",
      function () {
        navigate(`/fms/menu`);
      }
    );
  };

  const reportSuccessOffline = () => {
    Report.success(
      `OFFLINE : Has been recorded on local.`,
      "Berhasil di tersimpan di Lokal anda",
      "Ok",
      function () {
        form.resetFields();
      }
    );
  };

  const saveDelivery = async (data) => {
    try {

      const token = await requestToken();

      const formData = new FormData();

      // Upload files: take the final file after watermarking (we bound Upload to form)
      const flowFiles = data.flow_attachment ?? [];
      const operatorFiles = data.attachment_operator ?? [];

      if (flowFiles[0]?.originFileObj) {
        formData.append("flow_attachment", flowFiles[0].originFileObj);
      }
      if (operatorFiles[0]?.originFileObj) {
        formData.append("attachment_operator", operatorFiles[0].originFileObj);
      }

      // Prepare JSON (exclude Upload fields)
      const jsonData = { ...data };
      delete jsonData.flow_attachment;
      delete jsonData.attachment_operator;

      formData.append("json_data", JSON.stringify(jsonData));

      await API.CreateRequestDelivery(
        HeaderFarms(token),
        formData
      );

      infoCompleteAndRedirect();
    } catch (err) {
      console.error("saveDelivery error:", err.response?.status, err);

      if (err.code === "ECONNABORTED") {
        reportSuccessOffline();
      } else {
        if (!isOnline) {
            if (err.response?.status === 500) {
            Report.warning(
              `Gagal menyimpan data ke lokal`,
              JSON.stringify(err.response.data),
              "Okay"
            );
          } else if (err.response?.status === 503) {
            console.error("Network error detected: offline or network issues");
            reportSuccessOffline();
          }
        } else {
          console.error("Non-network error occurred");
          const messageError = err.response?.data?.error?.[0]?.name || "An error occurred while saving the document";
          Report.warning("Error", messageError, "OK");
        }
      }
    } finally {
      Loading.remove();
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFinish = async (data) => {
    if (isSubmitting || submitButtonRef.current?.disabled) {
      message.warning("Sedang diproses, mohon tunggu...");
      return;
    }

    setIsSubmitting(true);
    if (submitButtonRef.current) {
      submitButtonRef.current.disabled = true;
    }

    Confirm.show(
      "Important Confirmation",
      "Are you sure?",
      "Yes",
      "No",
      async () => {
        try {
          Loading.hourglass("Mohon tunggu sedang di proses.. ");
          await saveDelivery(data);
        } catch (error) {
          console.error("onFinish error:", error);
        } finally {
          // 3. Pastikan state dan tombol di-reset di semua kondisi
          Loading.remove();
          // Tombol akan di-reset di dalam `finally` milik saveDelivery
          // agar tetap nonaktif selama proses API call berlangsung.
        }
      },
      () => {
        // Aktifkan kembali jika pengguna membatalkan
        setIsSubmitting(false);
        if (submitButtonRef.current) {
          submitButtonRef.current.disabled = false;
        }
      }
    );
  };

  const onFinishFailed = () => message.warning("Mohon lengkapi formulir.");

  // ---------- Effects ----------
  useEffect(() => {
    (async () => {
      const pos = await getGeoLocation();
      setGeo(pos);
    })();
  }, []); // (Fix) only once – duplicated earlier

  useEffect(() => {
    requestToken()
      .then(getOperator)
      .catch(() => { });

    form.setFieldsValue({ fuel_type_delivery: "Bio Solar B35" });

    // Domain-based backdate policy (extend as needed)
    const host = window.location.hostname;
    switch (host) {
      case "backdaterf.citrapalu.net":
      case "backdatedr.citrapalu.net":
        setBackDate(true);
        break;
      default:
        setBackDate(true);
    }
  }, [form]);

  const fuelLostHandle = (event) => {
    const checked = event.target.checked;
    setFuelLoss(checked);
    // keep a machine-friendly flag in payload
    form.setFieldsValue({ fueloss: checked ? 1 : 0 });
  };

  return (
    <>
      <Detector
        render={({ online }) => {
          setIsOnline(online);
          return null;
        }}
      />
      <Navbar title="Create" back subtitle="Fuel System" navigate="/fms/menu" />

      <div className="container-fluid white-back-cr">
        <Form
          form={form}
          name="fuel_delivery"
          initialValues={{ remember: true, date: moment(), time: moment() }}
          onChange={handleFormChange}
          encType="multipart/form-data"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          size="middle"
          layout="vertical"
        >
          <div className="container-fluid p-2" style={{ alignSelf: "center" }}>
            <div className="row">
              <div className="col-md-12">
                <Collapse className="collapse_sr" expandIconPosition="end" accordion={false} bordered defaultActiveKey={["info"]}>
                  <Panel
                    style={{ background: "#fce8b6" }}
                    header={<b style={{ fontSize: "18px", fontWeight: "bold" }}>Informasi Penyerahan</b>}
                    key="info"
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={8} xl={5}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Date</span>}
                          name="date"
                          rules={[{ required: true, message: "Please input date" }]}
                        >
                          {/* Date only – keep TimePicker separate */}
                          <DatePicker
                            style={{ pointerEvents: backDate ? "auto" : "none" }}
                            format="YYYY-MM-DD"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8} xl={8}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Time</span>}
                          name="time"
                          rules={[{ required: true, message: "Please input time" }]}
                        >
                          <TimePicker
                            style={{ pointerEvents: backDate ? "auto" : "none" }}
                            showNow={false}
                            format="HH:mm"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={20}>
                      <Col xs={{ span: 24 }} sm={8} xl={8}>
                        <Form.Item
                          label={
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              Shift
                            </span>
                          }
                          name="shift"
                          rules={[
                            {
                              required: true,
                              message: "Wajib Isi",
                            },
                          ]}
                        >
                          <Radio.Group defaultValue="Shift">
                            <Radio value="SIANG">SIANG</Radio>
                            <Radio value="MALAM">MALAM</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={20}></Row>
                    <Row gutter={20}>
                      <Col xs={24} sm={10} xl={10}>
                        <div style={{ marginBottom: 4, maxWidth: 500, fontSize: 12, color: "#09ab67" }}>
                          * Pemilihan FT / Storage otomatis hitung penyaluran periode.
                        </div>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Penerima ( FT / Tangki )</span>}
                          name="fuel_truck"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <DistributionSelect size="large" onChange={handleDistributionChange} placeholder="Pilih Penerima" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={10} xl={10}>
                        <div style={{ marginBottom: 4, opacity: 0, maxWidth: 500, fontSize: 12, color: "#09ab67" }}>
                          * Transfer antar FT / Storage diperlukan fuel issued
                        </div>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Sumber ( Tangki / FT)</span>}
                          name="location_storage"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <DistributionSelect placeholder="Pilih Sumber" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={20}>
                      <Col xs={24} sm={11} xl={11}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Nama Operator / Driver</span>}
                          name="operator_id"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <Select
                            placeholder="Select"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={handleChangeOperator}
                            ref={operatorInputRef}
                            clearIcon={false}
                            // suffixIcon={
                            //   form.getFieldValue("operator_id") ? (
                            //     <CheckCircleOutlined style={{ color: "green", fontSize: 18 }} />
                            //   ) : (
                            //     <CloseCircleOutlined style={{ color: "red", fontSize: 18 }} />
                            //   )
                            // }
                          >
                            {operators.map((item) => (
                              <Select.Option key={item.employee_id} value={item.employee_id}>
                                {item.fullname} - {item.employee_id}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}> Foto Operator</span>}
                          name="attachment_operator"
                          valuePropName="fileList"
                          getValueFromEvent={normFile}
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <Upload
                            listType="picture"
                            accept="image/*;capture=camera"
                            capture={isAndroid ? "environment" : undefined}
                            fileList={fileListOperator}
                            onChange={(info) => {
                              handleUploadOperator(info);
                            }}
                            beforeUpload={() => false}
                          >
                            {fileListOperator.length === 0 && (
                              <Button loading={loadingOperator} icon={<CameraTwoTone style={{ fontSize: 19 }} />}>Buka Kamera</Button>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={20}>
                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Jenis Bahan Bakar</span>}
                          name="fuel_type_delivery"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <Radio.Group defaultValue="Bio Solar B35">
                            <Radio value="Bio Solar B35">Bio Solar B35</Radio>
                            <Radio value="Benzine">Bensin</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Quantity yang diminta</span>}
                          name="quantity_request"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber type="number" step={0.1} addonAfter="L" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>

                <Collapse className="collapse_sr" expandIconPosition="end" accordion={false} bordered defaultActiveKey={["check"]}>
                  <Panel
                    style={{ background: "#fce8b6" }}
                    header={<b style={{ fontSize: 18, fontWeight: "bold" }}>Pemeriksaan</b>}
                    key="check"
                  >
                    <Row gutter={20} className="mt-3">
                      <Col xs={24} sm={7} xl={7}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Kondisi Tangki</span>}
                          name="tank_condition"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <Radio.Group>
                            <Radio value="Blank">Kosong</Radio>
                            <Radio value="Filled">Terisi</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={15} xl={15}>
                        <div style={{ marginBottom: 4, maxWidth: 500, fontSize: 12, color: "#09ab67" }}>
                          * Perubahan periode akan otomatis hitung penyaluran
                        </div>
                        <Form.Item
                          label={<span style={{ fontSize: 14, fontWeight: "bold" }}>Periode Penyaluran Terakhir dan Sekarang</span>}
                          name="issued_period"
                          rules={[{ required: true, message: "Please input date" }]}
                        >
                          <RangePicker
                            showTime
                            onOk={handleDistributionChange}
                            placeholder={["Penyaluran Terakhir", "Penyaluran Hari ini"]}
                            format="YYYY-MM-DD HH:mm"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={20}>
                      <Col xs={24} sm={10} xl={10}>
                        <div style={{ marginBottom: 4, fontSize: 12, color: "#888" }}>
                          * Total Penyaluran ke Unit / Alat
                        </div>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Total Penyaluran Terakhir</span>}
                          name="issued_period_before"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber type="number" step={0.1} addonAfter="L" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={13} xl={11}>
                        <div style={{ marginBottom: 4, maxWidth: 500, fontSize: 12, color: "#09ab67" }}>
                          * Total sisa tanki pengisian terakhir setelah pengisian ke unit
                        </div>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Sisa Minyak Dalam Tangki</span>}
                          name="remaining_fuel_period"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber
                            type="number"
                            step={0.1}
                            size="large"
                            addonAfter="L"
                            addonBefore={
                              <Checkbox onChange={fuelLostHandle} checked={fuelLoss} style={{ fontSize: 16, fontWeight: "bold" }}>
                                Fuel Loss
                              </Checkbox>
                            }
                          />
                        </Form.Item>
                        <Form.Item name="fueloss" hidden>
                          <Input />
                        </Form.Item>
                      </Col>

                      {fuelLoss && (
                        <Col xs={24} sm={6} xl={6}>
                          <Form.Item
                            label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Deskripsi Fuel Loss</span>}
                            name="fueloss_description"
                            rules={[{ required: true, message: "Wajib Isi" }]}
                          >
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>

                    <Row>
                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Sounding Awal</span>}
                          name="sounding_before"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber style={{ width: 130 }} type="number" step={0.1} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Sounding Akhir</span>}
                          name="sounding_after"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber style={{ width: 130 }} type="number" step={0.1} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={20}>
                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Angka Flowmeter</span>}
                          name="flowmeter_total"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber type="number" step={0.1} addonAfter="L" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={10} xl={10}>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}> Foto Flowmeter</span>}
                          name="flow_attachment"
                          valuePropName="fileList"
                          getValueFromEvent={normFile}
                          rules={[{ required: false }]}
                        >
                          <Upload
                            listType="picture"
                            accept="image/*;capture=camera"
                            capture={isAndroid ? "environment" : undefined}
                            fileList={fileListMeter}
                            onChange={handleUploadMeter}
                            beforeUpload={() => false}
                          >
                            {fileListMeter.length === 0 && (
                              <Button loading={loadingFlow} icon={<CameraTwoTone style={{ fontSize: 19 }} />}>Buka Kamera</Button>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={10} xl={10}>
                        <div style={{ fontSize: 12, color: "#09ab67" }}>* Setelah pengisian dengan kalkulasi sisa minyak</div>
                        <Form.Item
                          label={<span style={{ fontSize: 16, fontWeight: "bold" }}>Isi Tangki Sekarang</span>}
                          name="issued_total"
                          rules={[{ required: true, message: "Wajib Isi" }]}
                        >
                          <InputNumber type="number" step={0.1} addonAfter="L" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row className="justify-content-center">
                      <Col>
                        <Form.Item className="mt-3">
                          <Button
                            loading={isSubmitting}
                            ref={submitButtonRef} // 4. Kaitkan ref ke tombol
                            disabled={isSubmitting}
                            type="primary"
                            size="large"
                            htmlType="submit"
                            icon={<CheckCircleOutlined />}
                          >
                            Save
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <Layout.Footer style={{ textAlign: "center", background: "#F9F9F9" }}>
        ©2024 Information Technology
      </Layout.Footer>
    </>
  );
}

export default CreateRequestDeliveryFuel;
